import React, { useState, useEffect } from "react";
import { Modal, Button } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { getRequest, postRequest, putRequest } from "../../system/server-call";
import { ToastifyService } from "../../services";
import { getUserData } from "../../system";
import ActionsDataTable from "../../shared/ActionsDataTable";
import { modeConstants } from "../../constants";

const Companies = () => {
  const dispatch = useDispatch();
  
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Modals state
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showUsersListModal, setShowUsersListModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  // Filter & Grid State for ActionsDataTable
  const [gridState, setGridState] = useState({
    column: "",
    type: "",
    search: null,
    filters: [],
  });

  // Form states
  const [companyForm, setCompanyForm] = useState({
    name: "",
    email: "",
    phone_number: "",
    address: "",
    license_type: "monthly",
    license_start_date: "",
    license_end_date: "",
  });

  const [userForm, setUserForm] = useState({
    name: "",
    email: "",
    phone_number: "",
  });

  const currentUser = getUserData();

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    dispatch({ type: "SPINNER_ACTIVATE_SUCCESS", data: true });
    try {
      const response = await getRequest("companies");
      if (response.data && response.data.data) {
        setCompanies(response.data.data);
      }
    } catch (error) {
      ToastifyService.showError(error.response?.data?.message || "Failed to fetch companies");
    } finally {
      dispatch({ type: "SPINNER_ACTIVATE_SUCCESS", data: false });
    }
  };

  const fetchCompanyUsers = async (companyId) => {
    dispatch({ type: "SPINNER_ACTIVATE_SUCCESS", data: true });
    try {
      const response = await getRequest(`companies/${companyId}/users`);
      if (response.data && response.data.data) {
        setCompanyUsers(response.data.data);
      }
    } catch (error) {
      ToastifyService.showError("Failed to fetch users");
    } finally {
      dispatch({ type: "SPINNER_ACTIVATE_SUCCESS", data: false });
    }
  };

  const handleCompanySubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    dispatch({ type: "SPINNER_ACTIVATE_SUCCESS", data: true });
    try {
      if (isEditMode) {
        await putRequest(`companies/${selectedCompany.id}`, companyForm, true);
        ToastifyService.showSuccess("Company updated successfully");
      } else {
        await postRequest("companies", companyForm, true);
        ToastifyService.showSuccess("Company registered successfully");
      }
      setShowCompanyModal(false);
      fetchCompanies();
    } catch (error) {
      ToastifyService.showError(error.response?.data?.message || "An error occurred");
    } finally {
      setIsSubmitting(false);
      dispatch({ type: "SPINNER_ACTIVATE_SUCCESS", data: false });
    }
  };

  const handleUserSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    dispatch({ type: "SPINNER_ACTIVATE_SUCCESS", data: true });
    try {
      await postRequest(`companies/${selectedCompany.id}/users`, userForm, true);
      ToastifyService.showSuccess("User registered successfully");
      setShowUserModal(false);
      setUserForm({ name: "", email: "", phone_number: "" });
      fetchCompanyUsers(selectedCompany.id);
    } catch (error) {
      ToastifyService.showError(error.response?.data?.message || "An error occurred");
    } finally {
      setIsSubmitting(false);
      dispatch({ type: "SPINNER_ACTIVATE_SUCCESS", data: false });
    }
  };

  const handleToggleActive = async (company) => {
    dispatch({ type: "SPINNER_ACTIVATE_SUCCESS", data: true });
    try {
      await putRequest(`companies/${company.id}/toggle-active`, {}, true);
      ToastifyService.showSuccess("Company status updated");
      fetchCompanies();
    } catch (error) {
      ToastifyService.showError("Failed to update status");
    } finally {
      dispatch({ type: "SPINNER_ACTIVATE_SUCCESS", data: false });
    }
  };

  const formatDateForInput = (dateStr) => {
    if (!dateStr) return "";
    return String(dateStr).split(" ")[0].split("T")[0];
  };

  const openAddCompany = () => {
    setSelectedCompany(null);
    setIsEditMode(false);
    setCompanyForm({
      name: "",
      email: "",
      phone_number: "",
      address: "",
      license_type: "monthly",
      license_start_date: new Date().toISOString().split("T")[0],
      license_end_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
    });
    setShowCompanyModal(true);
  };

  const openEditCompany = (company) => {
    setSelectedCompany(company);
    setIsEditMode(true);
    setCompanyForm({
      name: company.name || "",
      email: company.email || "",
      phone_number: company.phone_number || "",
      address: company.address || "",
      license_type: company.license_type ? String(company.license_type).toLowerCase().trim() : "monthly",
      license_start_date: formatDateForInput(company.license_start_date),
      license_end_date: formatDateForInput(company.license_end_date),
    });
    setShowCompanyModal(true);
  };

  const openAddUser = (company) => {
    setSelectedCompany(company);
    setUserForm({ name: "", email: "", phone_number: "" });
    setShowUserModal(true);
  };

  const openViewUsers = (company) => {
    setSelectedCompany(company);
    fetchCompanyUsers(company.id);
    setShowUsersListModal(true);
  };

  const calculateEndDate = (type, startDate) => {
    if (!startDate) return "";
    const start = new Date(startDate);
    if (isNaN(start.getTime())) return "";
    
    if (type === "monthly") {
      start.setMonth(start.getMonth() + 1);
    } else if (type === "yearly") {
      start.setFullYear(start.getFullYear() + 1);
    }
    return start.toISOString().split("T")[0];
  };

  const handleLicenseTypeChange = (e) => {
    const type = e.target.value;
    const endDate = calculateEndDate(type, companyForm.license_start_date);
    setCompanyForm({
      ...companyForm,
      license_type: type,
      license_end_date: endDate,
    });
  };

  const handleStartDateChange = (e) => {
    const startDate = e.target.value;
    const endDate = calculateEndDate(companyForm.license_type, startDate);
    setCompanyForm({
      ...companyForm,
      license_start_date: startDate,
      license_end_date: endDate,
    });
  };

  const getLicenseStatus = (company) => {
    if (!company.is_active) return { text: "Inactive", variant: "secondary" };
    const end = new Date(company.license_end_date);
    const now = new Date();
    if (end < now) return { text: "Expired", variant: "danger" };
    
    // Check if expiring in 7 days
    const diff = end.getTime() - now.getTime();
    const days = diff / (1000 * 3600 * 24);
    if (days <= 7) return { text: `Expiring soon (${Math.ceil(days)}d)`, variant: "warning" };
    
    return { text: "Active", variant: "success" };
  };

  // Filter columns aligned to company table fields
  const filterColumns = [
    { key: "name", label: "Company Name", type: "string" },
    { key: "email", label: "Email Address", type: "string" },
    { key: "phone_number", label: "Phone Number", type: "string" },
    { key: "license_type", label: "License Type (monthly/yearly)", type: "string" },
  ];

  const columns = [
    {
      name: "Company Name",
      selector: (row) => row.name,
      sortable: true,
      width: "160px",
      cell: (row) => <span className="fw-semibold text-dark">{row.name}</span>,
    },
    {
      name: "Contact Info",
      width: "220px",
      cell: (row) => (
        <div style={{ whiteSpace: "normal", wordBreak: "break-all" }}>
          <div className="text-dark" style={{ fontSize: "0.85rem" }}>{row.email || "N/A"}</div>
          <div className="text-muted" style={{ fontSize: "0.75rem" }}>{row.phone_number || "N/A"}</div>
        </div>
      ),
    },
    {
      name: "License Type",
      selector: (row) => row.license_type,
      sortable: true,
      width: "120px",
      cell: (row) => (
        <span className="badge bg-info text-uppercase py-1.5 px-2.5 rounded-pill text-dark" style={{ fontSize: "0.7rem" }}>
          {row.license_type}
        </span>
      ),
    },
    {
      name: "Start Date",
      selector: (row) => row.license_start_date,
      sortable: true,
      width: "110px",
      cell: (row) => row.license_start_date ? new Date(row.license_start_date).toLocaleDateString() : "N/A",
    },
    {
      name: "Expiry Date",
      selector: (row) => row.license_end_date,
      sortable: true,
      width: "110px",
      cell: (row) => row.license_end_date ? new Date(row.license_end_date).toLocaleDateString() : "N/A",
    },
    {
      name: "License Status",
      width: "130px",
      cell: (row) => {
        const status = getLicenseStatus(row);
        return (
          <span className={`badge bg-${status.variant} py-1.5 px-2.5 rounded-pill`} style={{ fontSize: "0.7rem" }}>
            {status.text}
          </span>
        );
      },
    },
    {
      name: "Actions",
      right: true,
      width: "180px",
      cell: (row) => (
        <div className="d-flex gap-1.5">
          <Button size="sm" variant="outline-secondary" title="View/Manage Users" onClick={() => openViewUsers(row)}>
            <i className="bi bi-people"></i>
          </Button>
          <Button size="sm" variant="outline-primary" title="Add User" onClick={() => openAddUser(row)}>
            <i className="bi bi-person-plus"></i>
          </Button>
          <Button size="sm" variant="outline-info" title="Edit Company" onClick={() => openEditCompany(row)}>
            <i className="bi bi-pencil"></i>
          </Button>
          <Button 
            size="sm" 
            variant={row.is_active ? "outline-warning" : "outline-success"} 
            title={row.is_active ? "Deactivate" : "Activate"}
            onClick={() => handleToggleActive(row)}
          >
            <i className={row.is_active ? "bi bi-lock" : "bi bi-unlock"}></i>
          </Button>
        </div>
      ),
    },
  ];

  // Client-side filtering logic
  const filteredCompanies = companies.filter((c) => {
    if (!gridState.filters || gridState.filters.length === 0) return true;
    
    return gridState.filters.every((filter) => {
      const fieldVal = String(c[filter.columnKey] || "").toLowerCase();
      const filterVal = String(filter.value || "").toLowerCase();
      
      switch (filter.op) {
        case "Contains":
          return fieldVal.includes(filterVal);
        case "StartsWith":
          return fieldVal.startsWith(filterVal);
        case "EndsWith":
          return fieldVal.endsWith(filterVal);
        case "EqualTo":
          return fieldVal === filterVal;
        default:
          return true;
      }
    });
  });

  if (!currentUser || !currentUser.is_super_admin) {
    return <div className="p-4 text-center"><h3>Access Denied. Super Admin only.</h3></div>;
  }

  return (
    <div className="container-fluid py-2">
      <ActionsDataTable
        columns={columns}
        filterColumns={filterColumns}
        data={filteredCompanies}
        setGridState={setGridState}
        gridState={gridState}
        pagination={true}
        setShowModal={(show) => {
          if (!show) setShowCompanyModal(false);
        }}
        setMode={(m) => {
          if (m === "add" || m === modeConstants.ADD_MODE) openAddCompany();
        }}
        allowCreation={true}
        allowFilters={true}
        allowToolbar={true}
        title="Companies"
      />

      {/* Register/Edit Company Modal */}
      <Modal show={showCompanyModal} onHide={() => setShowCompanyModal(false)} size="lg" backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title className="fw-bold">{isEditMode ? "Edit Company License" : "Register New Company"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={handleCompanySubmit}>
            <div className="row g-3 mb-4">
              <div className="col-12 col-md-6">
                <div className="form-floating">
                  <input
                    id="companyName"
                    type="text"
                    className="form-control"
                    placeholder="Company Name"
                    required
                    value={companyForm.name}
                    onChange={(e) => setCompanyForm({ ...companyForm, name: e.target.value })}
                  />
                  <label htmlFor="companyName">Company Name</label>
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div className="form-floating">
                  <input
                    id="companyEmail"
                    type="email"
                    className="form-control"
                    placeholder="Company Email"
                    value={companyForm.email}
                    onChange={(e) => setCompanyForm({ ...companyForm, email: e.target.value })}
                  />
                  <label htmlFor="companyEmail">Company Email</label>
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div className="form-floating">
                  <input
                    id="companyPhone"
                    type="text"
                    className="form-control"
                    placeholder="Phone Number"
                    value={companyForm.phone_number}
                    onChange={(e) => setCompanyForm({ ...companyForm, phone_number: e.target.value })}
                  />
                  <label htmlFor="companyPhone">Phone Number</label>
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div className="form-floating">
                  <input
                    id="companyAddress"
                    type="text"
                    className="form-control"
                    placeholder="Address"
                    value={companyForm.address}
                    onChange={(e) => setCompanyForm({ ...companyForm, address: e.target.value })}
                  />
                  <label htmlFor="companyAddress">Address</label>
                </div>
              </div>
              
              <div className="col-12 mt-4">
                <h6 className="fw-semibold text-primary mb-3">License Configuration</h6>
              </div>

              <div className="col-12 col-md-4">
                <div className="form-floating">
                  <select
                    id="licenseType"
                    className="form-select"
                    value={companyForm.license_type}
                    onChange={handleLicenseTypeChange}
                  >
                    <option value="monthly">Monthly</option>
                    <option value="yearly">Yearly</option>
                  </select>
                  <label htmlFor="licenseType">License Type</label>
                </div>
              </div>
              <div className="col-12 col-md-4">
                <div className="form-floating">
                  <input
                    id="licenseStart"
                    type="date"
                    max="9999-12-31"
                    className="form-control"
                    placeholder="Start Date"
                    required
                    value={companyForm.license_start_date}
                    onChange={handleStartDateChange}
                  />
                  <label htmlFor="licenseStart">Start Date</label>
                </div>
              </div>
              <div className="col-12 col-md-4">
                <div className="form-floating">
                  <input
                    id="licenseEnd"
                    type="date"
                    max="9999-12-31"
                    className="form-control"
                    placeholder="End Date / Expiry"
                    required
                    value={companyForm.license_end_date}
                    onChange={(e) => setCompanyForm({ ...companyForm, license_end_date: e.target.value })}
                  />
                  <label htmlFor="licenseEnd">End Date / Expiry</label>
                </div>
              </div>
            </div>

            <div className="d-flex justify-content-end footer-modal">
              <Button variant="outline-secondary" onClick={() => setShowCompanyModal(false)} className="btn-footer" disabled={isSubmitting}>
                Cancel
              </Button>
              <Button type="submit" variant="success" className="btn-footer ms-2" disabled={isSubmitting}>
                Save Changes
              </Button>
            </div>
          </form>
        </Modal.Body>
      </Modal>

      {/* Add Company User Modal */}
      <Modal show={showUserModal} onHide={() => setShowUserModal(false)} size="lg" backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title className="fw-bold">Add User for {selectedCompany?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={handleUserSubmit}>
            <div className="row g-3 mb-4">
              <div className="col-12 col-md-6">
                <div className="form-floating">
                  <input
                    id="userName"
                    type="text"
                    className="form-control"
                    placeholder="Full Name"
                    required
                    value={userForm.name}
                    onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                  />
                  <label htmlFor="userName">Full Name</label>
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div className="form-floating">
                  <input
                    id="userEmail"
                    type="email"
                    className="form-control"
                    placeholder="Email Address"
                    required
                    value={userForm.email}
                    onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                  />
                  <label htmlFor="userEmail">Email Address</label>
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div className="form-floating">
                  <input
                    id="userPhone"
                    type="text"
                    className="form-control"
                    placeholder="Phone Number"
                    value={userForm.phone_number}
                    onChange={(e) => setUserForm({ ...userForm, phone_number: e.target.value })}
                  />
                  <label htmlFor="userPhone">Phone Number</label>
                </div>
              </div>
            </div>

            <div className="d-flex justify-content-end footer-modal">
              <Button variant="outline-secondary" onClick={() => setShowUserModal(false)} className="btn-footer" disabled={isSubmitting}>
                Cancel
              </Button>
              <Button type="submit" variant="success" className="btn-footer ms-2" disabled={isSubmitting}>
                Register User
              </Button>
            </div>
          </form>
        </Modal.Body>
      </Modal>

      {/* View Company Users Modal */}
      <Modal show={showUsersListModal} onHide={() => setShowUsersListModal(false)} size="lg" backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title className="fw-bold">Registered Users: {selectedCompany?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="table-responsive mb-4">
            <table className="table hover borderless align-middle mb-0">
              <thead>
                <tr className="text-muted" style={{ borderBottom: "1px solid rgba(0,0,0,0.08)" }}>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Role</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {companyUsers.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="text-center py-4 text-muted">
                      No admin users registered for this company.
                    </td>
                  </tr>
                ) : (
                  companyUsers.map((u) => (
                    <tr key={u.id} style={{ borderBottom: "1px solid rgba(0,0,0,0.05)" }}>
                      <td className="fw-semibold text-dark py-3">{u.name}</td>
                      <td className="text-dark">{u.email}</td>
                      <td className="text-dark">{u.phone_number || "N/A"}</td>
                      <td>
                        <span className="badge bg-warning text-dark py-2 px-3 rounded-pill" style={{ fontSize: "0.75rem" }}>
                          Company Admin
                        </span>
                      </td>
                      <td>
                        <span className={`badge bg-${u.is_active ? "success" : "danger"} py-2 px-3 rounded-pill`} style={{ fontSize: "0.75rem" }}>
                          {u.is_active ? "Active" : "Inactive"}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="d-flex justify-content-end footer-modal">
            <Button variant="outline-secondary" onClick={() => setShowUsersListModal(false)} className="btn-footer">
              Close
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default Companies;
